import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import PriceChart from './PriceChart';
import { Boxes, Search, ChevronDown, ChevronRight, Tag, Loader2 } from 'lucide-react';

/**
 * Admin-only inventory browser. Shows every store's products with:
 * - Item code (e.g. MSRK-00001-I-0001)
 * - HSN India + International codes
 * - Tier badge
 * - Expand row → live price chart (last 90 days)
 */
export default function AdminInventory() {
  const [stores, setStores] = useState([]);
  const [filter, setFilter] = useState('');
  const [openRows, setOpenRows] = useState({});
  const [products, setProducts] = useState({});  // {storeId: [products]}
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => {
    api.get('/stores').then(r => setStores(r.data)).catch(() => {});
  }, []);

  const togglePerStore = async (storeId) => {
    if (openRows[storeId]) {
      setOpenRows(o => ({ ...o, [storeId]: false }));
      return;
    }
    setOpenRows(o => ({ ...o, [storeId]: true }));
    if (!products[storeId]) {
      setLoadingId(storeId);
      try {
        const r = await api.get(`/stores/${storeId}`);
        setProducts(p => ({ ...p, [storeId]: r.data.products || [] }));
      } finally { setLoadingId(null); }
    }
  };

  const filtered = stores.filter(s => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (s.name || '').toLowerCase().includes(q) || (s.store_code || '').toLowerCase().includes(q) || (s.village || '').toLowerCase().includes(q);
  });

  return (
    <div className="mt-5 space-y-3">
      <div className="card-soft p-4 flex flex-wrap gap-2 items-center">
        <Boxes className="w-5 h-5 text-[#0D47A1]"/>
        <div className="font-heading font-bold">Master inventory · item codes · HSN · price charts</div>
        <div className="relative ml-auto flex-1 max-w-[260px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-ink/40"/>
          <input data-testid="inv-search" value={filter} onChange={e=>setFilter(e.target.value)} placeholder="Filter by store / code / village" className="w-full pl-8 pr-3 py-2 text-xs rounded-lg border border-border focus:border-[#0D47A1] outline-none"/>
        </div>
      </div>

      <div className="card-soft p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F4F6] text-ink/70 text-xs uppercase tracking-wider">
            <tr><th className="px-3 py-2 text-left w-10"></th><th className="px-3 py-2 text-left">Store code</th><th className="px-3 py-2 text-left">Store</th><th className="px-3 py-2 text-left">Tier</th><th className="px-3 py-2 text-left">Category</th><th className="px-3 py-2 text-left">Village</th><th className="px-3 py-2 text-right">Trust</th></tr>
          </thead>
          <tbody>
            {filtered.map(s => (
              <>
                <tr key={s.id} data-testid={`inv-store-${s.id}`} className="border-t border-border hover:bg-[#FFF8EE] cursor-pointer" onClick={() => togglePerStore(s.id)}>
                  <td className="px-3 py-2">{openRows[s.id] ? <ChevronDown className="w-4 h-4"/> : <ChevronRight className="w-4 h-4"/>}</td>
                  <td className="px-3 py-2 font-mono text-xs font-bold text-[#1B5E20]">{s.store_code || '—'}</td>
                  <td className="px-3 py-2 font-semibold">{s.name}</td>
                  <td className="px-3 py-2"><TierPill tier={s.tier}/></td>
                  <td className="px-3 py-2 text-xs uppercase">{s.category}</td>
                  <td className="px-3 py-2 text-xs">{s.village}</td>
                  <td className="px-3 py-2 text-right text-xs font-bold">{s.trust?.score ?? '—'}</td>
                </tr>
                {openRows[s.id] && (
                  <tr key={`${s.id}-products`}>
                    <td colSpan={7} className="px-3 py-3 bg-[#FBF8F1]">
                      {loadingId === s.id ? <div className="flex items-center gap-2 text-sm text-ink/60"><Loader2 className="w-4 h-4 animate-spin"/>Loading inventory…</div> :
                        <ProductsList products={products[s.id] || []}/>}
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TierPill({ tier }) {
  const map = {
    retailer: { cls: 'bg-blue-50 text-blue-800 border-blue-200', label: 'Retailer' },
    wholesaler: { cls: 'bg-purple-50 text-purple-800 border-purple-200', label: 'Wholesaler' },
    factory_outlet: { cls: 'bg-amber-50 text-amber-900 border-amber-200', label: 'Factory outlet' },
  };
  const t = map[tier] || map.retailer;
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${t.cls}`}><Tag className="w-2.5 h-2.5"/>{t.label}</span>;
}

function ProductsList({ products }) {
  const [openChartId, setOpenChartId] = useState(null);
  if (products.length === 0) return <div className="text-sm text-ink/40 py-4">No products yet.</div>;
  return (
    <div className="space-y-2">
      {products.map(p => (
        <div key={p.id} data-testid={`inv-product-${p.id}`} className="rounded-lg border border-border bg-white p-3">
          <div className="flex items-center gap-3 flex-wrap">
            {p.image_url && <img src={p.image_url} alt="" className="w-12 h-12 rounded-md object-cover border border-border"/>}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm">{p.name}</span>
                <span className="font-mono text-[10px] text-[#0D47A1] bg-[#E3F2FD] px-1.5 py-0.5 rounded">{p.item_code || '—'}</span>
              </div>
              <div className="text-[11px] text-ink/60 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                {p.hsn_india ? <span><b>HSN-India:</b> {p.hsn_india}</span> : <span className="text-ink/30">No HSN-India</span>}
                {p.hsn_intl ? <span><b>HS-Intl:</b> {p.hsn_intl}</span> : <span className="text-ink/30">No HS-Intl</span>}
                <span>Stock: <b>{p.stock}</b></span>
                <span>Brand: {p.brand || '—'}</span>
                <span>Grade: {p.quality_grade || '—'}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono font-bold text-base">₹{p.price}</div>
              <button data-testid={`toggle-chart-${p.id}`} onClick={() => setOpenChartId(openChartId === p.id ? null : p.id)} className="mt-1 text-[11px] font-semibold text-[#1B5E20] hover:underline">
                {openChartId === p.id ? 'Hide' : 'Show'} 90d chart
              </button>
            </div>
          </div>
          {openChartId === p.id && (
            <div className="mt-2">
              <PriceChart productId={p.id} height={160}/>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
